import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useRealtimeContext } from '@utils/providers/realtime-context.tsx';
import { useRealtimeEditorContext } from '@utils/providers/realtime-editor-context.tsx';
import { RealtimeBody } from '@components/editors/realtime-editor/body/realtime-body.tsx';

/* eslint-disable no-restricted-syntax -- Semantic editor colors (recording/streaming status, playback cues, subtitle palette, pill foregrounds on tinted backgrounds) must stay stable across themes */

export default function RealtimeTranscriptionPanel() {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const { currentPartial, partialHistory, connectionState, finals } =
    useRealtimeContext();
  const { activeTab } = useRealtimeEditorContext();
  const partialsRef = useRef<HTMLDivElement>(null);
  const isUserAtBottomRef = useRef(true);

  const isConnected = connectionState === 'connected';
  const isConnecting =
    connectionState === 'connecting' || connectionState === 'loading';
  const hasData =
    finals.length > 0 || currentPartial || partialHistory.length > 0;

  const handlePartialsScroll = useCallback(() => {
    const el = partialsRef.current;
    if (!el) return;
    const threshold = 40;
    isUserAtBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  useEffect(() => {
    if (
      partialsRef.current &&
      activeTab === 'partials' &&
      isUserAtBottomRef.current
    ) {
      partialsRef.current.scrollTop = partialsRef.current.scrollHeight;
    }
  }, [currentPartial, partialHistory.length, activeTab]);

  return (
    <div
      className="flex flex-col rounded-b-lg overflow-hidden h-full border"
      style={{
        backgroundColor: getColour('secondLayer'),
        borderColor: getColour('border')
      }}
    >
      {/* Tab Content */}
      <div
        className="flex-1 flex flex-col overflow-hidden m-1.5 rounded-b-lg shadow-inner border"
        style={{
          backgroundColor: getColour('baseLayer'),
          border: getColour('borderPrimary')
        }}
      >
        {!isConnected && !isConnecting && !hasData ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md px-6 py-8">
              <>
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{
                    backgroundColor:
                      activeTab === 'partials'
                        ? theme === 'dark'
                          ? 'rgba(107,114,128,0.10)'
                          : 'rgba(107,114,128,0.06)'
                        : theme === 'dark'
                          ? 'rgba(107,114,128,0.10)'
                          : 'rgba(107,114,128,0.06)'
                  }}
                >
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke={
                      activeTab === 'partials'
                        ? theme === 'dark'
                          ? '#6b7280'
                          : '#6b7280'
                        : theme === 'dark'
                          ? '#6b7280'
                          : '#6b7280'
                    }
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                </div>
                <h2
                  className="text-base font-semibold mb-2 tracking-tight"
                  style={{ color: getColour('normalText') }}
                >
                  {t('ready-to-start')}
                </h2>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t(
                    'configure-your-connection-settings-on-the-left-and-click-connect-to-begin-live-transcription'
                  )}
                </p>
              </>
            </div>
          </div>
        ) : isConnecting && !hasData ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md px-6 py-8">
              <div className="mb-6 relative">
                <div
                  className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent mx-auto"
                  style={{ borderColor: getColour('activeText') }}
                ></div>
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ top: '4px' }}
                >
                  <div
                    className="w-8 h-8 rounded-full animate-pulse"
                    style={{ backgroundColor: getColour('activeText') }}
                  ></div>
                </div>
              </div>
              <h3
                className="text-base font-semibold mb-2 tracking-tight"
                style={{ color: getColour('normalText') }}
              >
                {connectionState === 'loading'
                  ? t('loading-stream')
                  : t('connecting')}
                ...
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: getColour('inactiveText') }}
              >
                {t('waiting-for-server-to-start-processing')}...
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Partials Tab */}
            {activeTab === 'partials' && (
              <div
                ref={partialsRef}
                role="log"
                aria-live="polite"
                aria-atomic="false"
                aria-label={t('live-stream')}
                className="flex-1 overflow-y-auto p-4"
                onScroll={handlePartialsScroll}
              >
                {partialHistory.length === 0 && !currentPartial ? (
                  <div className="flex items-center justify-center h-full">
                    <p
                      className="italic text-sm"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('waiting-for-partial-data')}...
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Locked/committed partials */}
                    {partialHistory.map((text, index) => (
                      <div key={index}>
                        <div
                          className="p-3 rounded-2xl mb-1 shadow-sm border-l-4"
                          style={{
                            backgroundColor: getColour('thirdLayer'),
                            borderLeftColor: getColour('border'),
                            color: getColour('normalText')
                          }}
                        >
                          <span className="font-medium text-sm leading-relaxed">
                            {text}
                          </span>
                        </div>
                        {(index < partialHistory.length - 1 ||
                          currentPartial) && (
                          <div
                            className="my-1.5 border-t"
                            style={{ borderColor: getColour('border') }}
                          />
                        )}
                      </div>
                    ))}

                    {/* Active current partial */}
                    {currentPartial ? (
                      <div
                        className="p-3 rounded-2xl mb-2 border-l-2 transition-colors duration-150"
                        style={{
                          backgroundColor: getColour('secondLayer'),
                          borderLeftColor: getColour('normalText'),
                          color: getColour('normalText')
                        }}
                      >
                        <span className="font-medium text-sm leading-relaxed">
                          {currentPartial}
                        </span>
                      </div>
                    ) : isConnected ? (
                      <div className="flex items-center gap-2 px-3 py-2 mt-1">
                        <span
                          className="w-2 h-2 rounded-full animate-pulse"
                          style={{
                            backgroundColor: getColour('inactiveText')
                          }}
                        />
                        <span
                          className="text-xs italic"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t('listening')}...
                        </span>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            )}

            {/* Finals Tab */}
            {activeTab === 'finals' && (
              <div className="flex-1 overflow-hidden">
                <RealtimeBody />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
